import { Link } from "react-router-dom";
import { Banknote, CreditCard, Lock } from "lucide-react";
import { euro } from "@/lib/api";
import { SITE, waLink } from "@/lib/site";
import { InstagramIcon, WhatsAppIcon } from "@/components/site/BrandIcons";

const PayOption = ({ id, active, disabled, icon: I, title, text, onClick }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    data-testid={`booking-pay-${id}-option`}
    className={`card-soft flex w-full items-start gap-4 border p-5 text-left transition-colors ${
      active ? "border-gold bg-gold-light" : "border-slate-200 hover:border-slate-300"
    } ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
  >
    <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${active ? "border-gold" : "border-slate-300"}`}>
      {active && <span className="h-2.5 w-2.5 rounded-full bg-gold" />}
    </span>
    <span className="flex-1">
      <span className="flex items-center gap-2 font-medium text-ink"><I className="h-4 w-4 text-gold" /> {title}</span>
      <span className="mt-1 block text-sm text-slate-500">{text}</span>
    </span>
  </button>
);

export const ReviewStep = ({ f, set, price, errors }) => (
  <div className="grid gap-6" data-testid="booking-step-review">
    <div>
      <p className="field-label">Payment</p>
      <div className="grid gap-3">
        <PayOption
          id="online"
          icon={CreditCard}
          active={f.payment_method === "online"}
          disabled={price == null}
          onClick={() => set("payment_method", "online")}
          title="Pay online now"
          text={price == null ? "Available for fixed-price routes only." : `Secure card payment of ${euro(price)} via Stripe.`}
        />
        <PayOption id="driver" icon={Banknote} active={f.payment_method === "driver"} onClick={() => set("payment_method", "driver")} title="Pay the driver" text="Cash or card on the day of your transfer." />
      </div>
    </div>
    <label className="flex items-start gap-3 text-sm text-slate-600">
      <input type="checkbox" className="mt-1 h-4 w-4 accent-[#C9A24B]" checked={f.accept_terms} onChange={(e) => set("accept_terms", e.target.checked)} data-testid="booking-terms-checkbox" />
      <span>
        I agree to the <Link to="/terms" target="_blank" className="text-ink underline decoration-gold underline-offset-4">Terms</Link> and{" "}
        <Link to="/privacy" target="_blank" className="text-ink underline decoration-gold underline-offset-4">Privacy Policy</Link>.
      </span>
    </label>
    {errors.accept_terms && <p className="field-error -mt-4" data-testid="accept_terms-error">{errors.accept_terms}</p>}
    <p className="flex items-center gap-2 text-xs text-slate-500"><Lock className="h-3.5 w-3.5" /> Card details are handled securely by Stripe. We never see them.</p>
  </div>
);

const Row = ({ k, v }) => (
  <div className="flex justify-between gap-4 py-2 text-sm">
    <span className="text-slate-500">{k}</span>
    <span className="text-right text-ink">{v}</span>
  </div>
);

export const SummaryCard = ({ pickup, dropoff, f, price }) => (
  <aside className="card-soft border border-slate-200 bg-white p-6 md:p-8 lg:sticky lg:top-28" data-testid="booking-summary">
    <span className="eyebrow">Your transfer</span>
    <div className="mt-5 space-y-1 border-b border-slate-100 pb-4">
      <p className="text-xs text-slate-500">From</p>
      <p className="font-serif text-xl text-ink" data-testid="summary-pickup">{pickup || "—"}</p>
      <p className="pt-2 text-xs text-slate-500">To</p>
      <p className="font-serif text-xl text-ink" data-testid="summary-dropoff">{dropoff || "—"}</p>
    </div>
    <div className="divide-y divide-slate-100">
      <Row k="Date" v={f.date && f.time ? `${f.date} · ${f.time}` : f.date || "—"} />
      <Row k="Passengers" v={f.passengers} />
      <Row k="Suitcases" v={f.luggage} />
      {f.child_seats && <Row k="Child seats" v={`${f.child_seat_count} (free)`} />}
      {f.meet_greet && <Row k="Meet & greet" v="Name sign" />}
    </div>
    <div className="mt-5 flex items-end justify-between border-t border-gold-border pt-5">
      <span className="text-sm text-slate-500">Total, per vehicle</span>
      <span className="font-serif text-4xl text-ink" data-testid="booking-price">{euro(price)}</span>
    </div>
    {price == null && pickup && dropoff && <p className="mt-2 text-xs text-slate-500">We will confirm your personal quote quickly by email or WhatsApp.</p>}
    <div className="mt-6 space-y-2 border-t border-slate-100 pt-5">
      <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Prefer to message?</p>
      <a href={waLink(`Hello Alpha Transfer, I would like to book a transfer.\nFrom: ${pickup || "-"}\nTo: ${dropoff || "-"}\nDate: ${f.date || "-"} ${f.time || ""}\nPassengers: ${f.passengers}\nFlight: ${f.flight_number || "-"}`)} target="_blank" rel="noreferrer" className="btn btn-outline w-full" data-testid="booking-whatsapp-button">
        <WhatsAppIcon className="h-4 w-4" /> Book via WhatsApp
      </a>
      <a href={SITE.instagramDm} target="_blank" rel="noreferrer" className="btn btn-outline w-full" data-testid="booking-instagram-button">
        <InstagramIcon className="h-4 w-4" /> Message us on Instagram
      </a>
    </div>
  </aside>
);
